import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Mail, Globe, ChevronDown, Loader2, ArrowUpRight } from "lucide-react";
import { Reveal, SectionTag } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  "Textiles, Apparel & Footwear",
  "Agro, Food & Seafood",
  "Home Furnishings & Décor",
  "Industrial, Safety & Packaging",
  "Multi-Category / Other",
];

const EMPTY = { name: "", email: "", company: "", country: "", category: CATEGORIES[0], message: "" };

const inputCls =
  "w-full bg-[#F8FAFE] border border-[#2E7FE0]/25 focus:border-[#2E7FE0] rounded-lg px-5 py-3.5 text-[#0A1B33] placeholder:text-[#54678A]/60 outline-none transition-colors duration-300 text-sm";

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry received. Our sourcing team will revert with a costed proposal.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Could not send your enquiry. Please email sales@nxgensourcing.com directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-28 lg:py-40 bg-white overflow-hidden" data-testid="contact-section">
      <div className="absolute top-0 right-[-15%] w-[600px] h-[600px] rounded-full bg-[#2E7FE0]/10 blur-[160px] pointer-events-none" />
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 relative z-10">
        <Reveal>
          <SectionTag index="09" label="Get In Touch" />
        </Reveal>

        <div className="grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="font-display font-semibold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#0A1B33] mb-8" data-testid="contact-heading">
                Let's build your <span className="text-[#2E7FE0]">India supply chain.</span>
              </h2>
              <p className="text-[#54678A] leading-relaxed mb-12 max-w-md" data-testid="contact-subcopy">
                Share your product specification, target price and expected volumes — we will
                revert with a costed sourcing proposal and an indicative production calendar.
              </p>
            </Reveal>

            <div className="space-y-7">
              {[
                { icon: MapPin, label: "Registered Office", value: "Kolkata, West Bengal, India", testid: "contact-address" },
                { icon: Mail, label: "Email", value: "sales@nxgensourcing.com", testid: "contact-email" },
                { icon: Globe, label: "Website", value: "www.nxgensourcing.com", testid: "contact-website" },
              ].map((c) => (
                <Reveal key={c.label}>
                  <div className="flex items-start gap-5" data-testid={c.testid}>
                    <span className="w-11 h-11 rounded-full border border-[#2E7FE0]/30 flex items-center justify-center shrink-0">
                      <c.icon className="w-5 h-5 text-[#2E7FE0]" />
                    </span>
                    <div>
                      <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#54678A] mb-1.5">{c.label}</p>
                      <p className="text-[#0A1B33] font-display font-medium">{c.value}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <Reveal delay={0.15} className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              className="glass-panel rounded-2xl p-8 lg:p-12"
              data-testid="contact-form"
            >
              <div className="grid sm:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="enquiry-name" className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#54678A] block mb-2.5">Full Name *</label>
                  <input id="enquiry-name" required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} data-testid="contact-name-input" />
                </div>
                <div>
                  <label htmlFor="enquiry-email" className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#54678A] block mb-2.5">Business Email *</label>
                  <input id="enquiry-email" required type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" className={inputCls} data-testid="contact-email-input" />
                </div>
                <div>
                  <label htmlFor="enquiry-company" className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#54678A] block mb-2.5">Company</label>
                  <input id="enquiry-company" value={form.company} onChange={set("company")} placeholder="Company name" className={inputCls} data-testid="contact-company-input" />
                </div>
                <div>
                  <label htmlFor="enquiry-country" className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#54678A] block mb-2.5">Country</label>
                  <input id="enquiry-country" value={form.country} onChange={set("country")} placeholder="Destination country" className={inputCls} data-testid="contact-country-input" />
                </div>
              </div>
              <div className="mb-6">
                <label htmlFor="enquiry-category" className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#54678A] block mb-2.5">Product Category</label>
                <div className="relative">
                  <select
                    id="enquiry-category"
                    value={form.category}
                    onChange={set("category")}
                    className={`${inputCls} appearance-none pr-12 cursor-pointer`}
                    data-testid="contact-category-select"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c} className="bg-white text-[#0A1B33]">{c}</option>
                    ))}
                  </select>
                  <ChevronDown className="w-4 h-4 text-[#54678A] absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
              </div>
              <div className="mb-8">
                <label htmlFor="enquiry-message" className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#54678A] block mb-2.5">Requirement *</label>
                <textarea
                  id="enquiry-message"
                  required
                  rows={5}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Product specification, target price, expected volumes and delivery timeline..."
                  className={`${inputCls} resize-none`}
                  data-testid="contact-message-input"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="group w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#2E7FE0] hover:bg-[#4a93eb] disabled:opacity-60 text-white font-display font-medium px-10 py-4 rounded-full transition-colors duration-300"
                data-testid="contact-submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Sending Enquiry...
                  </>
                ) : (
                  <>
                    Send Enquiry
                    <ArrowUpRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </>
                )}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
